// Who the current user is, and what they are allowed to do, resolved from every
// source the session actually has rather than from one that can silently vanish.
//
// Frank M. Booth, 10-20 Aug 2026. Toshia Vining's header loses her name, and the
// Pay Control column, the delete button and the ability to start a Q&A go with
// it. A 62-second screen recording on 20 Aug catches the header flipping —
// "Toshia Vining" on one page load, her profile's desk label on the next, same
// tab, same URL, same session.
//
// Everything she loses reads the role out of the persisted redux `signIn` slice
// (`cognitoRes.idToken.payload.given_name`). Everything that still works —
// the role line, resolving a Q&A — reads `userInfo`, which App.js refetches and
// retries. Thirty components read the slice; one reads `userInfo`. So the whole
// permission surface hung on the one source nothing repairs, and two rounds of
// fixes in August only ever corrected the header text.
//
// Both resolvers below fall through the same three sources. They are pure
// functions of the store (plus sessionStorage) so the ordering can be tested
// directly, without mounting anything.

import { claimsFromSessionToken, noteTokenFallback } from './tokenClaims';
import { ROLE_CLAIM, LEGACY_ROLE_CLAIM } from './userRole';

// Cognito stores "Toshia Vining " with a trailing space, and a whitespace-only
// claim must fall through rather than render as an empty header.
const usable = (value) =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;

const sliceClaims = (state) => state?.signIn?.signIn?.cognitoRes?.idToken?.payload;

// Reading the raw token means the persisted slice could not answer — the exact
// condition this whole change exists to survive. Recorded so the recovery is
// still visible after it stops being painful: once the role resolves, the user
// notices nothing and reports nothing, and this flag is the only evidence left
// that a session lost its store. It rides out on the X-Token-Claims header of
// the next get-user, which the same empty store is already guaranteeing.
//
// A write inside a selector, deliberately: it is a diagnostic flag, idempotent,
// and never read back into render.
const fromToken = (claim) => {
  const value = usable(claimsFromSessionToken()?.[claim]);
  if (value !== undefined) noteTokenFallback();
  return value;
};

// The role claim is moving from `given_name` to `custom:custom_role` (see
// utils/userRole.js). Within one source the new claim wins outright: after SSO
// federation `given_name` holds a first name, so it is only read when that same
// source has no `custom:custom_role` at all.
const roleFromClaims = (claims) =>
  usable(claims?.[ROLE_CLAIM]) ?? usable(claims?.[LEGACY_ROLE_CLAIM]);

/**
 * The role, which drives every permission gate in the app.
 *
 * `userInfo.role` leads because it is the one the server sends and App.js
 * repairs: get-user returns Cognito's `given_name` as `role`, so this is the
 * same value by a route that heals itself rather than one written once at login.
 * The slice comes next so a session whose profile has not loaded yet behaves
 * exactly as it did before, and the raw token last so a lost slice no longer
 * costs the user their permissions.
 */
export const resolveUserRole = (state) =>
  usable(state?.userInfo?.userInfo?.role) ??
  roleFromClaims(sliceClaims(state)) ??
  fromToken(ROLE_CLAIM) ??
  fromToken(LEGACY_ROLE_CLAIM);

/**
 * The display name.
 *
 * Deliberately the opposite order from the role. Every user has two names, set
 * at different moments by different people, and where they disagree the token
 * holds the person while the profile holds the desk label:
 *
 *   toshiav@frankbooth.com   token "Toshia Vining"   profile "Accounts Payable"
 *
 * Preferring the profile — which one release on 2026-08-17 did — replaced real
 * names with desk labels permanently. So the token leads, the raw token backs
 * it up when the slice is gone, and the profile remains the last resort it was
 * always meant to be.
 */
export const resolveUserName = (state) =>
  usable(sliceClaims(state)?.name) ??
  fromToken('name') ??
  usable(state?.userInfo?.userInfo?.user?.name);

/**
 * Whether this role is on the MANAGER DOCUMENT FLOW: uploads land in the role's
 * own queue, the role acts as first approver, and its delivery tickets are
 * eligible for self-approval. The company-level managerUploadFilePermission is
 * the master switch; the Manager role is on it by name, and a custom role joins
 * by having its managerUploadFlow permission switched on — which the backend
 * materialises as company.managerFlowRoles the moment the toggle is saved, so
 * this reads the same pair of fields the server's own gate reads.
 *
 * A state selector: `useSelector(hasManagerUploadFlow)`. Components that already
 * hold `userDetails` and `userRole` can call `isManagerUploadFlow(userDetails,
 * userRole)` instead.
 */
const MANAGER_ROLE = 'Manager';

export const isManagerUploadFlow = (userDetails, userRole) =>
  Boolean(userDetails?.company?.managerUploadFilePermission) &&
  (userRole === MANAGER_ROLE ||
    (userDetails?.company?.managerFlowRoles ?? []).includes(userRole));

export const hasManagerUploadFlow = (state) =>
  isManagerUploadFlow(
    state?.userInfo?.userInfo,
    resolveUserRole(state)
  );
